
import {Component, Vue, Watch} from "vue-property-decorator";
import {
  currentUserModule, exportsModule,
  queryModule,
  studyDefinitionModule,
  studyInstitutesModule, studyModule,
  studyPatientsModule
} from "@/store";
import {DataOptions, DataTableHeader} from "vuetify";
import {PriorityState} from "@/components/query/QueryStates";
import {MdbsCore} from "@/api/tsclient";
import {formatDate, formatDateTime} from "@/auxillary/date-format";
import {translate} from "@/auxillary/translation";
import ReferenceRedirectLine from "@/components/query/overview/ReferenceRedirectLine.vue";
import QueryOverviewTableAnswer from "@/components/query/overview/QueryOverviewTableAnswer.vue";
import QueryStateIcon from "@/components/query/icons/QueryStateIcon.vue";
import {deepCopy} from "@/auxillary";
import IQueryOverviewShortDto = MdbsCore.IQueryOverviewShortDto;
import IQueryDto = MdbsCore.IQueryDto;
import QueryTypeDefinitionDto = MdbsCore.QueryTypeDefinitionDto;
import {ellipsis100, ellipsis20} from "@/auxillary/string-format";
import QueryShortDto = MdbsCore.QueryShortDto;
import {startDownload} from "@/auxillary/export.helper";
import Rights = MdbsCore.Rights;
import {canSeeQueryAuditTrail} from "@/auxillary/audittrail.permissions";

interface ReferenceDefinition {
  hasDefinition: boolean
  displayKey: string
}

@Component({
  methods: {canSeeQueryAuditTrail},
  components: {QueryStateIcon, ReferenceRedirectLine, QueryOverviewTableAnswer},
  filters: {
    formatDate,
    formatDateTime,
    ellipsis20,
    ellipsis100,
    translate,
    formatNullable: function (value?: any | undefined): string {
      return value ?? "-";
    }
  }
})
export default class QueryOverviewTable extends Vue {
  items: Array<IQueryOverviewShortDto> = [];
  itemsTotal = 0;

  search: string = "";
  selectedPatientIdentifier: string | undefined = "";
  selectedInstituteIdentifier: string | undefined = "";

  expandedItem: IQueryDto | null = null;

  footerOptions: any = {
    'items-per-page-options':  [15, 30, 100, -1],
    'disable-pagination': false,
    'items-per-page-text': 'Queries pro Seite'
  };

  headers: DataTableHeader[] = [
    {
      text: "Prio",
      value: "priorityState",
      sortable: true,
      width: "70px"
    },
    {
      text: "Status",
      value: "stageState",
      sortable: true,
      width: "90px"
    },
    {
      text: "Query",
      value: "sequenceId",
      width: "110px",
      sortable: true,
      filterable: true
    },
    {
      text: "Erstellt",
      value: "creationDate",
      width: "100px",
      sortable: true,
      filterable: true
    },
    {
      text: "Antwort",
      value: "lastComment",
      width: "100px",
      sortable: true,
      filterable: true
    },
    {
      text: "Patient",
      value: "patientIdentifier",
      width: "150px",
      sortable: true,
      filterable: true
    },
    {
      text: "Zentrum",
      value: "studyInstituteIdentifier",
      sortable: false,
      width: "100px",
      filterable: true
    },
    {
      text: "Referenz",
      value: "referenceType",
      sortable: true,
      filterable: true,
      width: "100px"
    },
    {
      text: "Nachricht",
      value: "text",
      sortable: false,
      filterable: true
      // width: "100px"
    },
    {
      text: "Aktion",
      value: "action",
      sortable: false,
      filterable: false
    }
  ]

  options: DataOptions = {
    page: 1,
    itemsPerPage: 15,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: true,
    mustSort: false
  };

  get loading() {
    return queryModule.isLoading || studyPatientsModule.isLoading;
  }

  @Watch("loading")
  loadingChanged(loading: boolean) {
    this.footerOptions['disable-pagination'] = loading;
  }

  get patients() {
    return deepCopy(studyPatientsModule.studyPatients)
        .sort((s1, s2) => s1.identifier!.localeCompare(s2.identifier!));
  }

  get institutes() {
    return deepCopy(studyInstitutesModule.studyInstitutes)
        .sort((i1, i2) => i1.identifier!.localeCompare(i2.identifier!));
  }

  @Watch("options", {deep: true})
  async handlePagination() {
    await this.loadOverview();
  }

  async handleSearch() {
    this.options.page = 1;
    await this.loadOverview();
  }

  async handleSearchCleared() {
    this.search = ""
    this.options.page = 1;
    await this.loadOverview();
  }

  @Watch("selectedPatientIdentifier", {deep: true})
  async handlePatientSelection() {
    this.options.page = 1;
    await this.loadOverview();
  }

  @Watch("selectedInstituteIdentifier", {deep: true})
  async handleInstituteSelection() {
    this.options.page = 1;
    await this.loadOverview();
  }

  async loadOverview() {
    let {page, itemsPerPage, sortBy, sortDesc} = this.options;
      itemsPerPage = itemsPerPage == -1? this.itemsTotal : itemsPerPage;
    const response = await queryModule.loadQueryOverview({
      page,
      itemsPerPage,
      sortBy,
      sortDesc,
      studyPatientId: this.selectedPatientIdentifier,
      studyInstituteId: this.selectedInstituteIdentifier,
      searchText: this.search
    });

    this.itemsTotal = response.itemsTotal;
    this.items.updateValues(response.items);

    if (!studyPatientsModule.isLoaded) {
      await studyPatientsModule.loadAll(false);
    }

    if (studyInstitutesModule.studyInstitutes.length == 0) {
      await studyInstitutesModule.loadAll(false);
    }
  }

  get selectedPatient() {
    return this.selectedPatientIdentifier;
  }

  set selectedPatient(value: string | undefined) {
    this.selectedPatientIdentifier = value != "" ? value : undefined;
  }

  get selectedInstitute() {
    return this.selectedInstituteIdentifier;
  }

  set selectedInstitute(value: string | undefined) {
    this.selectedInstituteIdentifier = value != "" ? value : undefined;
  }


  get queryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
    const queryTypeDefinitions = studyDefinitionModule.queryTypeDefinitions
        .filter(qt => !qt.isDeleted)
        .filter(qt => this.canSeeQueryDefinition(qt));

    return queryTypeDefinitions;
  }

  canSeeQueryDefinition(queryDefinition: QueryTypeDefinitionDto) {
    if (!queryDefinition)
      return false;

    const canView = queryDefinition.definition.canView as Array<string>;
    if (!canView) return false;
    return canView.includes(currentUserModule.roleIdentifier);
  }

  queryTypeDefinition(query: IQueryDto): QueryTypeDefinitionDto | undefined {
    if (!query)
      return undefined;
    return this.queryTypeDefinitions.find(qf => qf.definitionType == query.queryTypeDefinitionType);
  }

  referenceId(query: IQueryDto): string | undefined {
    if (query.referenceType == "InstrumentDefinition" || query.referenceType == "Instrument")
      return query.instrumentId;
    if (query.referenceType == "VisitDefinition" || query.referenceType == "Visit")
      return query.visitId;
    if (query.referenceType == "PatientDefinition" || query.referenceType == "Patient")
      return query.patientId;
    if (query.referenceType == "StudyInstitute")
      return query.studyInstituteId;
    return "";
  }

  async handleItemExpanded(row: any) {
    const item = row.item as IQueryOverviewShortDto;
    const expanded = row.value as boolean;

    if (!expanded) return;

    this.expandedItem = await queryModule.loadQuery(item.id);
  }

  async reloadQueryOverview(query: IQueryDto) {
    if (!query) return;

    await this.loadOverview();
    this.expandedItem = await queryModule.loadQuery(query.id);
  }

  get priorityIcon() {
    return (item: IQueryOverviewShortDto) => {
      let icon = "fa-info-circle";
      let color = "primary";
      let toolTip = "normal";

      if (item.priorityState == PriorityState.HIGH) {
        icon = "fa-exclamation-circle";
        color = "error";
        toolTip = "hoch";
      }
      return {icon, color, toolTip}
    }
  }

  get lastComment() {
    return (item: IQueryOverviewShortDto) => {
      return {
        creationDate: item.lastComment?.creationDate,
        creatingUserName: item.lastComment?.creatingUserUserName,
        text: item.lastComment?.message.Message,
      }
    }
  }

  get referenceType() {
    return (item: IQueryOverviewShortDto) => {
      // return ReferenceTypeMapping
      //     .find(rtm => rtm.referenceType == item.referenceType)
      //     ?.text ?? item.referenceType;
      return item.referenceType;
    }
  }

  get referenceTypeDefinition() {
    return (referenceDefinitionId: string): ReferenceDefinition => {
      const result = {displayKey: "-", hasDefinition: false} as ReferenceDefinition;

      if (referenceDefinitionId == null) {
        return result;
      }

      const definition = studyDefinitionModule.dataFieldDefinitionById(referenceDefinitionId);
      if (!definition) return result;

      result.displayKey = definition.displayKey ?? "";
      result.hasDefinition = true;
      return result;
    }
  }

  hasPatient(query: IQueryDto) {
    if (query.patientPatientIdentifier == null || query.patientPatientIdentifier == undefined || query.patientPatientIdentifier == "")
      return false;
    return true;
  }


  hasInstrument(query: IQueryDto) {
    if (query.instrumentIdentifier == null || query.instrumentIdentifier == undefined || query.instrumentIdentifier == "")
      return false;
    return true;
  }

  handleRedirectInstrument(query: IQueryDto) {
    const route: any =
        !!query.visitId &&
        query.visitId != "00000000-0000-0000-0000-000000000000"
            ? {
              name: 'visitinstrumentedit',
              params: {
                patientId: query.patientId!,
                visitId: query.visitId!,
                instrumentId: query.instrumentId!
              }
            }
            : {
              name: 'standaloneinstrumentedit',
              params: {
                patientId: query.patientId!,
                instrumentId: query.instrumentId!
              }
            };

    this.$router.push(route);
  }

  handleRedirectPatient(query: IQueryDto) {
    const route: any = {
      name: 'patient',
      params: {
        patientId: query.patientId!,
      }
    };

    this.$router.push(route);
  }

  async generateQueriesOverviewExport() {
    let queries = this.items.map(q => new QueryShortDto(q));
    await exportsModule.generateQueriesOverviewExport({studyId: studyModule.study.id, queries: queries})
        .then(response => {
          if (response) {
            startDownload(response.data, response.fileName ?? "");
          }
        }).catch(console.error)
  }

  async generateUserQueriesExport() {
    await exportsModule.generateUserQueriesExport({studyId: studyModule.study.id})
        .then(response => {
          if (response) {
            startDownload(response.data, response.fileName ?? "");
          }
        }).catch(console.error)
  }

  get canGenerateQueriesOverviewExport() {
    return currentUserModule.hasRight(Rights.QueriesOverviewExportGetCmd);
  }

  get canGenerateUserQueriesExport() {
    return currentUserModule.hasRight(Rights.UserQueriesExportGetCmd);
  }

  async openAuditTrail(queryId: string){

    await this.navigate("queryaudittrail", {
      queryId: queryId
    });
  }

  async navigate(name: string, params: any) {
    try {
      return await this.$router.push({name, params});
    } catch (error) {
      if (error.name !== 'NavigationDuplicated')
        throw error;
    }
  }
}
